import { useEffect, useState } from "react";
import { MapContainer, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import { BASEMAPS, DIRECTORY_DEFAULT_BASEMAP, readBasemap, resolveBasemap, writeBasemap } from "../lib/basemap";
import { wrapLngNear } from "../lib/mapFit";
import MapControlPill from "./kit/MapControlPill";
import MapFullScreen from "./kit/MapFullScreen";
import MapFullScreenSync from "./kit/MapFullScreenSync";
import WrappingTileLayer from "./WrappingTileLayer";

/**
 * Issue #1152 (D225 item 9) — both pins of a duplicate pair on one small map.
 *
 * The merge is the most destructive verb on `/admin/grounds`, and the old
 * footnote sent the reviewer to the whole grounds map to find the two pins by
 * hand. This draws the pair itself: marker A, marker B, fitted so both are in
 * view. It is the directory's basemap and nothing else: no layer switcher, no
 * drawing, no click handlers. Zoom and attribution stay (D224 does not count
 * them as choices), and the tile credit is the source's own (`lib/basemap.js`).
 *
 * #1669 (D449, D467) — the map wears the one control, a pill with map/satellite
 * (no +/- box): full screen on top, then map/satellite. The inline map keeps its own height; only the overlay is full screen. The tiles
 * wrap, and a pair either side of the date line is drawn on one copy of the
 * world, so the fit frames the two pins and not the planet between them.
 */
const FIT_PADDING = 24;
const FIT_MAX_ZOOM = 17;

const isPoint = (p) => Number.isFinite(Number(p?.lat)) && Number.isFinite(Number(p?.lng)) && p?.lat !== null && p?.lng !== null;

const letterIcon = (letter) =>
  new L.DivIcon({
    className: "pair-map-marker",
    html: `<div style="width:26px;height:26px;border-radius:50%;background:#0B2E59;color:#fff;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;font:700 13px/1 sans-serif;">${letter}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });

const ICON_A = letterIcon("A");
const ICON_B = letterIcon("B");

/** `MapContainer`'s bounds only apply at first mount; this fits once both pins exist. */
function PairFitter({ a, b }) {
  const map = useMap();
  const [aLat, aLng] = a;
  const [bLat, bLng] = b;
  useEffect(() => {
    map.fitBounds(
      L.latLngBounds([
        [aLat, aLng],
        [bLat, bLng],
      ]),
      { padding: [FIT_PADDING, FIT_PADDING], maxZoom: FIT_MAX_ZOOM },
    );
  }, [map, aLat, aLng, bLat, bLng]);
  return null;
}

/**
 * #1176 — Tab stops. Order here: the map (one stop; Leaflet makes its container focusable and +/- and the arrow keys
 * zoom and pan it), then the control pill's full screen and map/satellite buttons (#1669), then the basemap's credit
 * link, then "Not a duplicate". Leaflet's own "Leaflet" prefix link is dropped. The credit link stays a stop:
 * `lib/basemap.js` trims the Esri credit to that link on purpose, so the full credit list is reachable only through it.
 */
function MapKeyboardStops({ label }) {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    container.setAttribute("role", "region");
    container.setAttribute("aria-label", label);
    map.attributionControl?.setPrefix(false);
  }, [map, label]);
  return null;
}

export default function PairMap({ a, b, height = 180 }) {
  const [chosen, setChosen] = useState(readBasemap);
  if (!isPoint(a) || !isPoint(b)) return null;
  const active = resolveBasemap(chosen, DIRECTORY_DEFAULT_BASEMAP);
  const basemap = BASEMAPS.find((m) => m.id === active);
  const chooseBasemap = (id) => {
    setChosen(id);
    writeBasemap(id);
  };
  const posA = [Number(a.lat), Number(a.lng)];
  // B on the copy of the world nearest A, so a pair across the date line is two close points.
  const posB = [Number(b.lat), wrapLngNear(Number(b.lng), posA[1])];
  const fit = (map) =>
    map.fitBounds(L.latLngBounds([posA, posB]), { padding: [FIT_PADDING, FIT_PADDING], maxZoom: FIT_MAX_ZOOM });
  return (
    <MapFullScreen testId="dupe-pair-map" controlInPill height={height}>
      {() => (
        <MapContainer center={posA} zoom={15} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false} zoomControl={false}>
          <WrappingTileLayer key={basemap.id} attribution={basemap.attribution} url={basemap.url} maxZoom={basemap.maxZoom} />
          <MapControlPill basemap={active} onBasemap={chooseBasemap} />
          <MapFullScreenSync onResized={fit} />
          <PairFitter a={posA} b={posB} />
          <MapKeyboardStops label={`Map of ${a.name ?? "record A"} and ${b.name ?? "record B"}. Press plus or minus to zoom.`} />
          {/* keyboard={false}: the pins are not Tab stops. They open nothing
              and a marker has no accessible name of its own; the record links
              above the map open each record (#1176). */}
          <Marker position={posA} icon={ICON_A} keyboard={false}>
            <Tooltip direction="top" offset={[0, -12]}>{`A: ${a.name ?? ""}`}</Tooltip>
          </Marker>
          <Marker position={posB} icon={ICON_B} keyboard={false}>
            <Tooltip direction="top" offset={[0, -12]}>{`B: ${b.name ?? ""}`}</Tooltip>
          </Marker>
        </MapContainer>
      )}
    </MapFullScreen>
  );
}
